import {StateController} from "@lit-app/state/src/state-controller";
import {html, HTMLTemplateResult} from "lit";
import {get} from "lit-translate";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {classMap} from "lit/directives/class-map.js";

import {appState} from "../../app.state";
import {NavigationHelper} from "../../helpers/navigation.helper";
import {AbstractLitElement} from "../abstract-lit.element";
import {ModeRouterController} from "../_reactive-controllers/mode-router.controller";
import {NavigationItem} from "../ha-navigation-item/navigation-item.interface";
import "../ha-navigation-item/ha-navigation-item.component";

/**
 * Mode-switch rendered as two icon-based nav items at the top of the sidebar,
 * visually consistent with the regular nav stack (icon prefix + label).
 *
 * Visual state derives from `appState.currentMode` (via StateController).
 * Click handler navigates via NavigationHelper, then asks the
 * ModeRouterController to re-sync from URL — the controller remains the
 * sole writer of `appState.currentMode` (TD-002 F6).
 *
 * Light DOM is inherited from AbstractLitElement (ADR-007).
 */
@customElement('ha-mode-switch')
export class HaModeSwitchComponent extends AbstractLitElement {

    @property({attribute: false})
    public modeController!: ModeRouterController;

    @property({type: Boolean, attribute: 'collapsed'})
    public isCollapsed = false;

    private _isProcessing = false;

    public constructor() {
        super();
        new StateController(this, appState);
    }

    protected render(): HTMLTemplateResult {
        const current = appState.currentMode;
        return html`
            ${this._renderModeLink('/by-board', 'wissen.todo-label', current === 'todo')}
            ${this._renderModeLink('/resource-management', 'wissen.resource-label', current === 'resource')}
            ${this._renderModeLink('/knowledge', 'wissen.mode-label', current === 'wissen')}
            ${this._renderModeLink('/linkview', 'wissen.linkview-label', current === 'linkview')}
            ${this._renderModeLink('/personal-organizer', 'wissen.personal-organizer-label', current === 'personal-organizer')}
        `;
    }

    /**
     * No `prefix` template — the icon glyph comes from CSS `:before` content
     * on `.ha-app__navigation-item__prefix`, keyed by `href` (todo_sidebar.css),
     * consistent with the sidebar nav pattern (todo_sidebar.css) and the
     * Font-Awesome-via-Unicode convention.
     */
    private _renderModeLink(path: string, labelKey: string, active: boolean): HTMLTemplateResult {
        const item: NavigationItem = {
            label: get(labelKey),
        };
        return html`
            <a class=${classMap({active, 'ha-mode-switch__item': true})}
               href=${path}
               title=${item.label}
               @click=${(e: MouseEvent) => this._onClick(e, path)}>
                <ha-navigation-item .item=${item}
                                    ?collapsed=${this.isCollapsed}
                ></ha-navigation-item>
            </a>
        `;
    }

    /**
     * True when the browser already sits on `path`. Trailing slashes are ignored
     * so `/by-board` and `/by-board/` are the same destination.
     */
    private _isCurrentPath(path: string): boolean {
        const strip = (value: string): string => value.replace(/\/+$/, '') || '/';
        return strip(window.location.pathname) === strip(path);
    }

    private async _onClick(event: MouseEvent, path: string): Promise<void> {
        event.preventDefault();
        if (this._isProcessing) return;

        // Compare against the current ROUTE, not the current mode. Bailing out on
        // `appState.currentMode === target` made the entry of the mode you are
        // already in inert — but a route can belong to a mode without being that
        // mode's landing page. /settings/translations counts as `todo`, so the
        // ToDo entry rendered active and its handler returned early, while
        // preventDefault() had already cancelled the browser's own navigation.
        // The result was a dead end: no way back to /by-board from that screen.
        // Clicking the entry for the page you are actually on stays a no-op.
        if (this._isCurrentPath(path)) return;

        this._isProcessing = true;
        try {
            NavigationHelper.goto(path);
            this.modeController.syncFromUrl();
        } catch (error) {
            console.error('[ha-mode-switch] navigation failed', error);
        } finally {
            this._isProcessing = false;
        }
    }
}
